import React from "react";

import classnames from "classnames";

import BodyText from "Theme/BodyText";
import { BodyMedium } from "Cobalt/BodyText";
import type { RadioProps, RadioGroupProps } from "Theme/RadioGroup";

import makeBem from "Utils/makeBem";

const bem = makeBem("CobaltRadioGroup");
export type CobaltRadioProps = RadioProps & {
  name: string;
  value: string;
  onChange: (value: string) => void;
};
export const Radio = ({ name, onChange, value, checked, children, inline, disabled = false }: CobaltRadioProps) => {
  const inputId = `radio-${name}-${value}`;

  const handleClick = () => {
    if (!disabled) {
      if (onChange) {
        onChange(value);
      } else {
        console.error("Radio: onChange is required");
      }
    }
  };

  return (
    <BodyMedium
      className={classnames(bem("option"), inline && bem("option", "inline"), disabled && bem("option", "disabled"))} // This onClick gets triggered via bubbling when input is clicked
      // We want the wrapper to handle the click, not the input
      // Because this allows us to click the "dead spaces" around the input
      // while still using the native, accessibility friendly element
      onClick={handleClick}
    >
      <input
        id={inputId}
        type="radio"
        name={name}
        value={value}
        disabled={disabled}
        checked={checked} // Provide a noop handler to suppress react warnings about readonly fields
        onClick={() => undefined}
        onChange={() => undefined}
      />
      <label htmlFor={inputId}>{children}</label>
    </BodyMedium>
  );
};
export type CobaltRadioGroupProps = RadioGroupProps & {
  name: string;

  /** NEW: onChange is required */
  onChange: (value: string) => void;
};
export const RadioGroup = ({ disabled = false, inline, options, id, name, value, onChange, ...rest }: CobaltRadioGroupProps) => {
  const mappedOptions = Array.isArray(options) ? options : Object.entries(options);
  return (
    <>
      {mappedOptions.map(([optionValue, label], i) => (
        <Radio {...rest} key={i} name={name} onChange={onChange} inline={inline} value={optionValue} checked={value === optionValue} disabled={disabled}>
          {label}
        </Radio>
      ))}
    </>
  );
};
